import { useState } from 'react';
import { RotateCw, Sparkles } from 'lucide-react';

interface PWAUpdatePromptBodyProps {
  /** App display name interpolated into the body copy. */
  appName: string;
  /** Called when the user clicks the Reload button — wired to `updateSW(true)`. */
  onReload: () => void;
}

/**
 * Body of the "new version available" toast surfaced by
 * `registerServiceWorker.tsx` when vite-plugin-pwa's `onNeedRefresh` fires.
 *
 * The Reload button calls `updateSW(true)` (passed in as `onReload`),
 * which makes the waiting service worker take control and reloads the
 * page. Once clicked, the button enters a "Reloading…" state but the
 * reload typically happens within a tick so the user rarely sees it.
 *
 * Shared verbatim across the three DomainBoost/PostExchange front-ends so
 * the update prompt looks identical regardless of which app you're in.
 */
export const PWAUpdatePromptBody = ({
  appName,
  onReload,
}: PWAUpdatePromptBodyProps) => {
  const [reloading, setReloading] = useState(false);

  const handleClick = () => {
    if (reloading) return;
    setReloading(true);
    onReload();
  };

  return (
    <div className="flex items-start gap-3 py-0.5">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#DBEAFE] to-[#BFDBFE] flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
        <Sparkles
          className="w-5 h-5 text-[#1D4ED8]"
          strokeWidth={2}
          aria-hidden="true"
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-[#020817] leading-tight">
          Update available
        </p>
        <p className="text-[11px] font-semibold text-[#6B7280] mt-0.5 leading-snug">
          A new version of {appName} is ready. Reload to use the latest build.
        </p>
        <button
          type="button"
          onClick={handleClick}
          disabled={reloading}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-bold text-white bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#1D4ED8] hover:to-[#1E40AF] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_2px_6px_rgba(29,78,216,0.3)] transition-all"
        >
          <RotateCw
            className={`w-3 h-3 ${reloading ? 'animate-spin' : ''}`}
            strokeWidth={2.5}
            aria-hidden="true"
          />
          {reloading ? 'Reloading…' : 'Reload'}
        </button>
      </div>
    </div>
  );
};

export default PWAUpdatePromptBody;
